/**
 * PX demo data universe — CORE entities, shared by every surface.
 *
 * ONE universe: the fictional Perpleks portfolio from the August-2026 mockup
 * deck (product/px-platform-mockups.md), carried forward verbatim from the
 * round-1 bake-off (design/prototypes/px-build-brief.md). Figures the deck
 * states are transcribed as given; entities the deck implies but does not name
 * (people beyond the named six, sibling projects) are invented ONCE here and
 * reused everywhere.
 *
 * RULES (build brief §0a):
 *  - ZERO literals in components: every figure, name, id and date renders from
 *    this module or a `content/<surface>.ts` module that imports it.
 *  - Ages/countdowns measure against NOW — never Date.now().
 *  - Surfaces never add to this file mid-wave; a missing fact is reported, not
 *    invented.
 *
 * CANONICAL TIME/STATE RESOLUTION (deck was per-slide, prototype is one world):
 *  - NOW = Thu 27 Aug 2026 09:00 CET (go-live 12 Nov is exactly "11 weeks").
 *  - Van Dijk's canonical phase = BUILD (3 of 7), per screen 3.1. The Migration
 *    surface (3.6) shows migration PREP running during Build (mapping + trial
 *    loads rehearse before the phase gate opens) — the deck's "PHASE 4" pill on
 *    3.6 is resolved in favour of one coherent hero record.
 */

import { ROLE_DEFINITIONS, type PrincipalSide, type RoleKey } from '@/access/roles'
import { type PhaseKey } from '@/modules/phases'

export const NOW = new Date('2026-08-27T09:00:00+02:00')

/** Format a deck-style figure with thin-space thousands: 4100 -> "4 100". */
export const thinSpace = (n: number): string =>
  n.toLocaleString('en-US').replace(/,/g, '\u202f')

export type OfficeCode = 'NL' | 'BE' | 'FR' | 'KS'

export interface Office {
  code: OfficeCode
  name: string
  city: string
  headcount: number
}

/** 62 people across four offices — the deck's resource-pool filter chips. */
export const OFFICES: readonly Office[] = [
  { code: 'NL', name: 'Netherlands', city: 'Utrecht', headcount: 21 },
  { code: 'BE', name: 'Belgium', city: 'Antwerp', headcount: 12 },
  { code: 'FR', name: 'France', city: 'Lyon', headcount: 9 },
  { code: 'KS', name: 'Kosovo · PX-People', city: 'Pristina', headcount: 20 },
] as const

export const GROUP_HEADCOUNT = 62

/**
 * The ten role keys and the seven delivery phases were re-homed to
 * `@/access/roles` and `@/modules/phases` by PX-W6.6 (spec §6.3): they are
 * product taxonomy, not seed. They are re-exported here so the ~40 authored
 * demo-content modules keep one import site, while product furniture imports
 * the product modules directly (invariant I-2).
 */
export type { RoleKey, PrincipalSide } from '@/access/roles'
export type { Phase, PhaseKey } from '@/modules/phases'
export { PHASES, phaseIndex } from '@/modules/phases'

export interface Person {
  id: string
  name: string
  initial: string
  office: OfficeCode
  role: string
  skills: readonly string[]
  currentProject: string | null
  loadPct: number
  freePct: number
  rate: number // €/h
  languages?: readonly string[]
  joined?: string
}

/**
 * Named people. The deck names six (Anouk, Pieter, Endrit, Arta, Julien,
 * Niels) + Margot (skills matrix), Yll (go-live); the rest are invented once.
 * Counts elsewhere still speak of 62 — the table shows a page of them.
 */
export const PEOPLE: readonly Person[] = [
  { id: 'anouk-de-vries', name: 'Anouk de Vries', initial: 'A', office: 'NL', role: 'Project manager', skills: ['delivery lead', 'retail', 'fit-gap'], currentProject: 'van-dijk', loadPct: 85, freePct: 15, rate: 110, languages: ['NL', 'EN'], joined: '2019' },
  { id: 'pieter-bakker', name: 'Pieter Bakker', initial: 'P', office: 'NL', role: 'Developer', skills: ['Odoo dev', 'pricing', 'promotions'], currentProject: 'van-dijk', loadPct: 94, freePct: 6, rate: 98, languages: ['NL', 'EN'], joined: '2020' },
  { id: 'niels-jansen', name: 'Niels Jansen', initial: 'N', office: 'NL', role: 'Manager', skills: ['practice lead', 'staffing'], currentProject: null, loadPct: 70, freePct: 30, rate: 120, languages: ['NL', 'EN'], joined: '2017' },
  { id: 'femke-de-boer', name: 'Femke de Boer', initial: 'F', office: 'NL', role: 'Consultant', skills: ['inventory', 'wms', 'retail'], currentProject: 'roosendaal', loadPct: 78, freePct: 22, rate: 95, languages: ['NL', 'EN'], joined: '2022' },
  { id: 'daan-visser', name: 'Daan Visser', initial: 'D', office: 'NL', role: 'Developer', skills: ['Odoo dev', 'integrations'], currentProject: 'vermeer', loadPct: 82, freePct: 18, rate: 92, languages: ['NL', 'EN'], joined: '2023' },
  { id: 'sanne-kuipers', name: 'Sanne Kuipers', initial: 'S', office: 'NL', role: 'HR', skills: ['people', 'hiring', 'cohorts'], currentProject: null, loadPct: 60, freePct: 40, rate: 80, languages: ['NL', 'EN'], joined: '2021' },
  { id: 'jeroen-smit', name: 'Jeroen Smit', initial: 'J', office: 'NL', role: 'Admin', skills: ['platform', 'access', 'audit'], currentProject: null, loadPct: 55, freePct: 45, rate: 90, languages: ['NL', 'EN'], joined: '2018' },
  { id: 'margot-claes', name: 'Margot Claes', initial: 'M', office: 'BE', role: 'Consultant', skills: ['payroll BE', 'social secretariat', 'hr'], currentProject: 'hasselt-zorg', loadPct: 96, freePct: 4, rate: 94, languages: ['NL', 'FR', 'EN'], joined: '2019' },
  { id: 'thomas-peeters', name: 'Thomas Peeters', initial: 'T', office: 'BE', role: 'Consultant', skills: ['accounting', 'invoicing'], currentProject: null, loadPct: 0, freePct: 100, rate: 88, languages: ['NL', 'FR', 'EN'], joined: '2024' },
  { id: 'lotte-willems', name: 'Lotte Willems', initial: 'L', office: 'BE', role: 'Analyst', skills: ['delivery intelligence', 'reporting'], currentProject: null, loadPct: 65, freePct: 35, rate: 85, languages: ['NL', 'EN'], joined: '2022' },
  { id: 'julien-roux', name: 'Julien Roux', initial: 'J', office: 'FR', role: 'Migration engineer', skills: ['migration', 'QA', 'retail data'], currentProject: 'lumiere', loadPct: 58, freePct: 42, rate: 96, languages: ['FR', 'EN'], joined: '2021' },
  { id: 'camille-laurent', name: 'Camille Laurent', initial: 'C', office: 'FR', role: 'Consultant', skills: ['manufacturing', 'mrp'], currentProject: 'atelier-fournier', loadPct: 74, freePct: 26, rate: 90, languages: ['FR', 'EN'], joined: '2022' },
  { id: 'hugo-moreau', name: 'Hugo Moreau', initial: 'H', office: 'FR', role: 'Project manager', skills: ['delivery lead', 'food service'], currentProject: 'lumiere', loadPct: 92, freePct: 8, rate: 105, languages: ['FR', 'EN'], joined: '2020' },
  { id: 'marc-dubois', name: 'Marc Dubois', initial: 'M', office: 'FR', role: 'Solution architect', skills: ['architecture', 'blueprints', 'retail'], currentProject: null, loadPct: 68, freePct: 32, rate: 125, languages: ['FR', 'EN'], joined: '2018' },
  { id: 'endrit-krasniqi', name: 'Endrit Krasniqi', initial: 'E', office: 'KS', role: 'Delivery consultant', skills: ['delivery', 'fit-gap'], currentProject: 'vermeer', loadPct: 82, freePct: 18, rate: 48, languages: ['SQ', 'EN'], joined: '2023' },
  { id: 'arta-berisha', name: 'Arta Berisha', initial: 'A', office: 'KS', role: 'Developer', skills: ['Odoo dev', 'migration (assisted)'], currentProject: 'van-dijk', loadPct: 45, freePct: 55, rate: 48, languages: ['SQ', 'EN'], joined: '2024' },
  { id: 'yll-gashi', name: 'Yll Gashi', initial: 'Y', office: 'KS', role: 'Engineer', skills: ['cutover', 'infrastructure'], currentProject: 'van-dijk', loadPct: 72, freePct: 28, rate: 52, languages: ['SQ', 'EN'], joined: '2023' },
  { id: 'blerta-hoxha', name: 'Blerta Hoxha', initial: 'B', office: 'KS', role: 'QA engineer', skills: ['QA', 'regression', 'UAT support'], currentProject: 'vermeer', loadPct: 80, freePct: 20, rate: 46, languages: ['SQ', 'EN'], joined: '2024' },
  { id: 'vlora-ahmeti', name: 'Vlora Ahmeti', initial: 'V', office: 'KS', role: 'Trainee consultant', skills: ['Competence Center · cohort 4'], currentProject: null, loadPct: 0, freePct: 100, rate: 35, languages: ['SQ', 'EN'], joined: '2026' },
] as const

export const personById = (id: string): Person => {
  const p = PEOPLE.find((x) => x.id === id)
  if (!p) throw new Error(`Unknown person: ${id}`)
  return p
}

/** Deck: "Over 90% — 3 people, named" · "On bench — 2 people, named". */
export const OVERLOADED_IDS = ['margot-claes', 'pieter-bakker', 'hugo-moreau'] as const
export const BENCH_IDS = ['thomas-peeters', 'vlora-ahmeti'] as const

/* ── Implementer firms, clients, contacts and the platform operator ─────── */

/**
 * TASK-9F6C8EBE. The prototype gained a firm axis so the super-admin lens has
 * something to span. Two firms exist and no more: the ANCHOR firm (Perpleks —
 * everything the rest of the prototype seeds belongs to it) and ONE second
 * implementer that owns clients and a licensed-module set AND NOTHING ELSE —
 * no projects, people, assignments, work packages, headcount, revenue,
 * contract value, utilisation or dates. It exists to prove the axis, appears
 * only on /admin/clients, and no figure is invented for it (spec I-15/I-16).
 *
 * Module ids are plain strings here on purpose: `architecture.ts` imports
 * `PhaseKey` from this module, so this module must never import it back.
 * `architecture.test.ts` asserts every id below resolves to a real registry row.
 */

/** The 44 live Rev A module ids — the anchor firm licenses all of them. */
const ALL_MODULE_IDS = [
  // L0 · Platform & Admin
  'access-manager', 'agent-module-registry', 'template-library', 'tenant-provisioning',
  'model-gateway', 'guardrails-policy', 'audit-log', 'metering-billing',
  // L1 · Organization
  'organization-profile', 'people-roles', 'contracts', 'client-portfolio', 'budget',
  'results', 'rates-capacity', 'firm-knowledge-base', 'integrations',
  // L2 · Client
  'client-profile', 'client-data-room', 'client-context-graph', 'access-sharing', 'client-pl',
  // L3 · Project / Engagement
  'project-workspace', 'work-packages', 'hours-timesheets', 'deliverables',
  'consultant-assignment', 'budget-vs-actual', 'margin-utilization', 'project-agent-crew',
  'deliverable-versioning',
  // L4 · Execution
  'triggers-scheduler', 'expert-agents', 'results-store', 'orchestrator', 'tools-connectors',
  'human-review-gate', 'run-log-cost', 'evaluation-feedback',
  // Cross-cutting
  'notifications-inbox', 'search', 'analytics-dashboards', 'permission-inheritance',
  'retention-compliance',
] as const

/** The second firm's licence: 24 of the 44. Design spec §2.7. */
const MEERKANT_MODULE_IDS = [
  'access-manager', 'agent-module-registry', 'template-library', 'model-gateway', 'audit-log',
  'metering-billing', 'organization-profile', 'people-roles', 'client-portfolio',
  'client-profile', 'client-data-room', 'access-sharing', 'project-workspace', 'work-packages',
  'hours-timesheets', 'deliverables', 'consultant-assignment', 'triggers-scheduler',
  'expert-agents', 'results-store', 'human-review-gate', 'run-log-cost', 'notifications-inbox',
  'search',
] as const

export interface Firm {
  id: string
  name: string
  country: string
  offices: number
  /** Exactly one row is the anchor — the firm the whole seeded universe belongs to. */
  anchor: boolean
  /** MODULE_REGISTRY ids the firm licenses. */
  installedModules: readonly string[]
}

export const FIRMS: readonly Firm[] = [
  { id: 'perpleks', name: 'Perpleks', country: 'NL', offices: OFFICES.length, anchor: true, installedModules: ALL_MODULE_IDS },
  { id: 'meerkant', name: 'Meerkant ERP', country: 'BE', offices: 2, anchor: false, installedModules: MEERKANT_MODULE_IDS },
] as const

export const ANCHOR_FIRM_ID = 'perpleks'

export const firmById = (id: string): Firm => {
  const f = FIRMS.find((x) => x.id === id)
  if (!f) throw new Error(`Unknown firm: ${id}`)
  return f
}

export interface ClientContact {
  id: string
  name: string
  initial: string
  role: string
  org: string
  clientId: string
}

export const CLIENT_CONTACTS: readonly ClientContact[] = [
  { id: 'client-sophie', name: 'Sophie van Dijk', initial: 'S', role: 'Sponsor', org: 'Van Dijk Retail Group', clientId: 'van-dijk-retail' },
  { id: 'client-ruben', name: 'Ruben Hendriks', initial: 'R', role: 'Key user · Store operations', org: 'Van Dijk Retail Group', clientId: 'van-dijk-retail' },
] as const

/**
 * Retained alias — the sponsor. 17 call sites across 7 content modules read
 * this name, so it stays exported with its original shape.
 */
export const CLIENT_CONTACT: ClientContact = CLIENT_CONTACTS[0]!

export const clientContactById = (id: string): ClientContact => {
  const c = CLIENT_CONTACTS.find((x) => x.id === id)
  if (!c) throw new Error(`Unknown client contact: ${id}`)
  return c
}

export interface Operator {
  id: string
  name: string
  initial: string
  role: string
  org: string
}

/** The super admin is a PLATFORM principal — deliberately not in PEOPLE. */
export const OPERATORS: readonly Operator[] = [
  { id: 'op-iris', name: 'Iris Lammers', initial: 'I', role: 'Platform operator', org: 'PX Platform' },
] as const

export const operatorById = (id: string): Operator => {
  const o = OPERATORS.find((x) => x.id === id)
  if (!o) throw new Error(`Unknown operator: ${id}`)
  return o
}

export interface Client {
  id: string
  name: string
  office: OfficeCode
  vertical: string
  odooVersion: 19 | 18 | 17
  /** ISO day the account opened. */
  since: string
  /** The implementer firm that owns the account. */
  firmId: string
  /** Per-client module ids this client has installed (drives the super-admin matrix). */
  installedModules: readonly string[]
}

/**
 * The 17 per-client installable modules, in the super-admin matrix's column
 * order. Every client's `installedModules` is a subset of this list.
 */
const PER_CLIENT_MODULE_IDS = [
  'template-library',
  'client-profile', 'client-data-room', 'client-context-graph', 'access-sharing', 'client-pl',
  'project-workspace', 'work-packages', 'hours-timesheets', 'deliverables',
  'consultant-assignment', 'budget-vs-actual', 'margin-utilization', 'project-agent-crew',
  'deliverable-versioning',
  'expert-agents', 'tools-connectors',
] as const

/** All 17 minus the named ids — keeps each client row readable and checkable. */
const perClientExcept = (...missing: readonly string[]): readonly string[] =>
  PER_CLIENT_MODULE_IDS.filter((id) => !missing.includes(id))

export const CLIENTS: readonly Client[] = [
  { id: 'van-dijk-retail', name: 'Van Dijk Retail Group', office: 'NL', vertical: 'Retail', odooVersion: 19, since: '2026-02-16', firmId: 'perpleks', installedModules: perClientExcept() },
  { id: 'vermeer-logistics', name: 'Vermeer Logistics', office: 'NL', vertical: 'Logistics', odooVersion: 19, since: '2026-01-19', firmId: 'perpleks', installedModules: perClientExcept('client-context-graph', 'client-pl') },
  { id: 'hasselt-zorg', name: 'Hasselt Zorg', office: 'BE', vertical: 'Healthcare', odooVersion: 18, since: '2026-04-20', firmId: 'perpleks', installedModules: perClientExcept('client-pl', 'margin-utilization', 'deliverable-versioning', 'tools-connectors') },
  { id: 'lumiere-maison', name: 'Lumière Maison', office: 'FR', vertical: 'Retail', odooVersion: 19, since: '2026-03-02', firmId: 'perpleks', installedModules: perClientExcept('client-pl') },
  { id: 'atelier-fournier', name: 'Atelier Fournier', office: 'FR', vertical: 'Manufacturing', odooVersion: 19, since: '2026-07-20', firmId: 'perpleks', installedModules: perClientExcept('client-context-graph', 'access-sharing', 'client-pl', 'budget-vs-actual', 'margin-utilization', 'project-agent-crew', 'deliverable-versioning', 'tools-connectors') },
  { id: 'roosendaal-foods', name: 'Roosendaal Foods', office: 'NL', vertical: 'Food service', odooVersion: 19, since: '2026-03-23', firmId: 'perpleks', installedModules: perClientExcept('client-context-graph', 'client-pl', 'project-agent-crew', 'tools-connectors') },
  { id: 'bergman-winkels', name: 'Bergman Winkels', office: 'NL', vertical: 'Retail', odooVersion: 18, since: '2025-03-10', firmId: 'perpleks', installedModules: perClientExcept('client-data-room', 'client-context-graph', 'client-pl', 'consultant-assignment', 'budget-vs-actual', 'margin-utilization', 'project-agent-crew', 'tools-connectors') },
  { id: 'brugge-bouw', name: 'Brugge Bouw', office: 'BE', vertical: 'Construction', odooVersion: 19, since: '2026-07-27', firmId: 'perpleks', installedModules: perClientExcept('client-context-graph', 'access-sharing', 'client-pl', 'deliverables', 'consultant-assignment', 'budget-vs-actual', 'margin-utilization', 'project-agent-crew', 'deliverable-versioning', 'expert-agents', 'tools-connectors') },
  { id: 'stroomdal-energie', name: 'Stroomdal Energie', office: 'BE', vertical: 'Utilities', odooVersion: 18, since: '2026-02-09', firmId: 'meerkant', installedModules: perClientExcept('client-context-graph', 'access-sharing', 'client-pl', 'budget-vs-actual', 'margin-utilization', 'project-agent-crew', 'deliverable-versioning', 'tools-connectors') },
  { id: 'kortrijk-textiel', name: 'Kortrijk Textiel', office: 'BE', vertical: 'Manufacturing', odooVersion: 17, since: '2026-05-11', firmId: 'meerkant', installedModules: perClientExcept('client-data-room', 'client-context-graph', 'access-sharing', 'client-pl', 'consultant-assignment', 'budget-vs-actual', 'margin-utilization', 'project-agent-crew', 'deliverable-versioning', 'expert-agents', 'tools-connectors') },
] as const

export const clientById = (id: string): Client => {
  const c = CLIENTS.find((x) => x.id === id)
  if (!c) throw new Error(`Unknown client: ${id}`)
  return c
}

export const clientsForFirm = (firmId: string): readonly Client[] =>
  CLIENTS.filter((c) => c.firmId === firmId)

/** Every per-client installable module, in the matrix's declared column order. */
export const perClientModuleIds = (): readonly string[] => PER_CLIENT_MODULE_IDS

export type ModuleInstallState = 'installed' | 'available' | 'not-licensed'

/**
 * The super-admin matrix cell, DERIVED — no cell value is ever seeded (seeding
 * ~170 of them would be ~170 invented figures). Total: every client × every
 * per-client module yields exactly one of the three declared states, so a cell
 * can never render blank.
 */
export const moduleStateFor = (client: Client, moduleId: string): ModuleInstallState =>
  client.installedModules.includes(moduleId)
    ? 'installed'
    : firmById(client.firmId).installedModules.includes(moduleId)
      ? 'available'
      : 'not-licensed'

/** The projects of one client account. */
export const projectsForClient = (clientId: string): readonly Project[] =>
  PROJECTS.filter((p) => p.clientId === clientId)

/* ── Projects (portfolio: Active 18 · AMS 24 · Won, not started 3) ──────── */

export type Health = 'On track' | 'At risk' | 'Blocked'

export interface Project {
  id: string
  /** Legacy display string, retained so no content module changes (I-5). */
  client: string
  /** ADDITIVE (TASK-9F6C8EBE) — the CLIENTS row this engagement belongs to. */
  clientId: string
  name: string
  odooVersion: 19 | 18 | 17
  vertical: string
  office: OfficeCode
  phase: PhaseKey
  health: Health
  leadId: string
  contractValue: number // €
  goLive: string // ISO date
  contractType: 'Fixed-scope rollout' | 'Time & materials' | 'Upgrade project' | 'AMS contract' | 'Discovery only'
  /** The hero record — followed on every surface. */
  followed?: boolean
}

export const PROJECTS: readonly Project[] = [
  { id: 'van-dijk', client: 'Van Dijk Retail Group', clientId: 'van-dijk-retail', name: 'Van Dijk Retail Group — Odoo 19 rollout', odooVersion: 19, vertical: 'Retail', office: 'NL', phase: 'build', health: 'On track', leadId: 'anouk-de-vries', contractValue: 480_000, goLive: '2026-11-12', contractType: 'Fixed-scope rollout', followed: true },
  { id: 'vermeer', client: 'Vermeer Logistics', clientId: 'vermeer-logistics', name: 'Vermeer Logistics — Odoo 19 rollout', odooVersion: 19, vertical: 'Logistics', office: 'NL', phase: 'test', health: 'At risk', leadId: 'anouk-de-vries', contractValue: 310_000, goLive: '2026-10-02', contractType: 'Fixed-scope rollout' },
  { id: 'hasselt-zorg', client: 'Hasselt Zorg', clientId: 'hasselt-zorg', name: 'Hasselt Zorg — Odoo 18 rollout', odooVersion: 18, vertical: 'Healthcare', office: 'BE', phase: 'design', health: 'Blocked', leadId: 'margot-claes', contractValue: 240_000, goLive: '2027-01-20', contractType: 'Fixed-scope rollout' },
  { id: 'lumiere', client: 'Lumière Maison', clientId: 'lumiere-maison', name: 'Lumière Maison — Odoo 19 rollout', odooVersion: 19, vertical: 'Retail', office: 'FR', phase: 'migrate', health: 'On track', leadId: 'hugo-moreau', contractValue: 350_000, goLive: '2026-11-27', contractType: 'Fixed-scope rollout' },
  { id: 'atelier-fournier', client: 'Atelier Fournier', clientId: 'atelier-fournier', name: 'Atelier Fournier — Odoo 19 rollout', odooVersion: 19, vertical: 'Manufacturing', office: 'FR', phase: 'discover', health: 'On track', leadId: 'hugo-moreau', contractValue: 190_000, goLive: '2027-03-05', contractType: 'Time & materials' },
  { id: 'roosendaal', client: 'Roosendaal Foods', clientId: 'roosendaal-foods', name: 'Roosendaal Foods — Odoo 19 upgrade', odooVersion: 19, vertical: 'Food service', office: 'NL', phase: 'build', health: 'At risk', leadId: 'femke-de-boer', contractValue: 120_000, goLive: '2026-10-16', contractType: 'Upgrade project' },
  { id: 'bergman', client: 'Bergman Winkels', clientId: 'bergman-winkels', name: 'Bergman Winkels — AMS', odooVersion: 18, vertical: 'Retail', office: 'NL', phase: 'maintain', health: 'On track', leadId: 'femke-de-boer', contractValue: 96_000, goLive: '2025-04-14', contractType: 'AMS contract' },
  { id: 'brugge-bouw', client: 'Brugge Bouw', clientId: 'brugge-bouw', name: 'Brugge Bouw — discovery', odooVersion: 19, vertical: 'Construction', office: 'BE', phase: 'discover', health: 'On track', leadId: 'margot-claes', contractValue: 45_000, goLive: '2027-05-01', contractType: 'Discovery only' },
  // TASK-9F6C8EBE — the anchor client's SECOND engagement, so the client-manager
  // landing spans two rows in genuinely different gate states. PORTFOLIO.active
  // (18) and every other deck total are unchanged: the board has always shown a
  // seeded page of a larger portfolio.
  { id: 'van-dijk-wonen', client: 'Van Dijk Retail Group', clientId: 'van-dijk-retail', name: 'Van Dijk Retail Group — second-wave rollout, Van Dijk Wonen', odooVersion: 19, vertical: 'Retail', office: 'NL', phase: 'design', health: 'On track', leadId: 'anouk-de-vries', contractValue: 265_000, goLive: '2027-02-19', contractType: 'Fixed-scope rollout' },
] as const

export const HERO_PROJECT_ID = 'van-dijk'
export const heroProject = (): Project => PROJECTS.find((p) => p.followed)!

export const projectById = (id: string): Project => {
  const p = PROJECTS.find((x) => x.id === id)
  if (!p) throw new Error(`Unknown project: ${id}`)
  return p
}

/** Portfolio counts beyond the seeded page of projects (deck figures). */
export const PORTFOLIO = {
  active: 18,
  ams: 24,
  wonNotStarted: 3,
  contractedValue: 2_900_000,
  gatesDueThisWeek: 7,
  gatesNeedingClientSignature: 3,
  atRiskOrBlocked: 3,
  reuseRatePct: 61,
} as const

/* ── Staffing and work packages ─────────────────────────────────────────── */

/**
 * TASK-9F6C8EBE. `ASSIGNMENTS` is the STAFFING RELATION — person × engagement
 * with an allocation percentage. It is deliberately NOT the same fact as
 * `VAN_DIJK.team` (the deck's four-person core-team panel on the project
 * overview): part-time allocations sit on top of the core team, which is why
 * van Dijk carries six assignment rows against a four-person panel. Surfaces
 * label this column "People" / "Staffed", never "Team", so the two figures can
 * never be read as one.
 *
 * The allocation percentages are checkable, not decorative: for every person
 * whose `currentProject` is non-null they sum EXACTLY to that person's
 * `loadPct`; for the four with no current project they are a subset of it, the
 * remainder being firm-internal work.
 */

/**
 * One staffing start per engagement. The design spec's assignment rows carry
 * person, role and allocation but no date, and `Assignment.since` is part of
 * the agreed contract — so a single day per engagement is seeded here rather
 * than 27 separate invented dates. Bergman reuses its own seeded AMS start.
 * No surface renders these; they exist so the relation is well-formed.
 */
const STAFFED_SINCE: Readonly<Record<string, string>> = {
  'van-dijk': '2026-03-02',
  'van-dijk-wonen': '2026-07-06',
  vermeer: '2026-02-02',
  'hasselt-zorg': '2026-05-04',
  lumiere: '2026-03-16',
  'atelier-fournier': '2026-08-03',
  roosendaal: '2026-04-06',
  bergman: '2025-04-14',
  'brugge-bouw': '2026-08-10',
}

export interface Assignment {
  projectId: string
  personId: string
  role: string
  allocationPct: number
  since: string
}

const assign = (projectId: string, personId: string, role: string, allocationPct: number): Assignment => ({
  projectId,
  personId,
  role,
  allocationPct,
  since: STAFFED_SINCE[projectId] ?? NOW.toISOString().slice(0, 10),
})

export const ASSIGNMENTS: readonly Assignment[] = [
  assign('van-dijk', 'anouk-de-vries', 'Delivery lead', 45),
  assign('van-dijk', 'pieter-bakker', 'Developer', 94),
  assign('van-dijk', 'endrit-krasniqi', 'Delivery consultant', 35),
  assign('van-dijk', 'arta-berisha', 'Developer', 45),
  assign('van-dijk', 'yll-gashi', 'Cutover engineer', 62),
  assign('van-dijk', 'femke-de-boer', 'Consultant · warehouse', 20),

  assign('van-dijk-wonen', 'anouk-de-vries', 'Delivery lead', 15),
  assign('van-dijk-wonen', 'marc-dubois', 'Solution architect', 30),
  assign('van-dijk-wonen', 'niels-jansen', 'Manager · commercial', 10),

  assign('vermeer', 'anouk-de-vries', 'Project manager', 25),
  assign('vermeer', 'daan-visser', 'Developer', 62),
  assign('vermeer', 'endrit-krasniqi', 'Delivery consultant', 47),
  assign('vermeer', 'blerta-hoxha', 'QA engineer', 60),

  assign('hasselt-zorg', 'margot-claes', 'Lead consultant', 76),
  assign('hasselt-zorg', 'marc-dubois', 'Solution architect', 23),

  assign('lumiere', 'hugo-moreau', 'Delivery lead', 72),
  assign('lumiere', 'julien-roux', 'Migration engineer', 58),

  assign('atelier-fournier', 'hugo-moreau', 'Project manager', 20),
  assign('atelier-fournier', 'camille-laurent', 'Consultant', 74),

  assign('roosendaal', 'femke-de-boer', 'Lead consultant', 45),
  assign('roosendaal', 'daan-visser', 'Developer', 20),
  assign('roosendaal', 'blerta-hoxha', 'QA engineer', 20),

  assign('bergman', 'femke-de-boer', 'AMS lead', 13),
  assign('bergman', 'yll-gashi', 'Engineer', 10),

  assign('brugge-bouw', 'margot-claes', 'Consultant', 20),
  assign('brugge-bouw', 'marc-dubois', 'Solution architect', 15),
  assign('brugge-bouw', 'lotte-willems', 'Analyst', 15),
] as const

export const assignmentsForProject = (projectId: string): readonly Assignment[] =>
  ASSIGNMENTS.filter((a) => a.projectId === projectId)

export const peopleOnProject = (projectId: string): readonly Person[] =>
  assignmentsForProject(projectId).map((a) => personById(a.personId))

export const projectsForPerson = (personId: string): readonly Project[] =>
  ASSIGNMENTS.filter((a) => a.personId === personId).map((a) => projectById(a.projectId))

export type WorkPackageStatus = 'backlog' | 'in-progress' | 'in-review' | 'blocked' | 'done'

/** THE declared kanban column order. No component re-declares it. */
export const WORK_PACKAGE_STATUSES: readonly WorkPackageStatus[] = [
  'backlog',
  'in-progress',
  'in-review',
  'blocked',
  'done',
] as const

export interface WorkPackage {
  id: string
  projectId: string
  title: string
  phase: PhaseKey
  status: WorkPackageStatus
  assigneeId: string | null
  /** false = internal; withheld from every client-side surface (I-3). */
  clientVisible: boolean
  hoursPlanned: number
  hoursSpent: number
  dueDate: string | null
  /** An AGENTS id, or null where no agent touched it. */
  agentAssistedBy: string | null
}

/**
 * 20 work packages. What the set guarantees, and `data.test.ts` asserts:
 *  - femke (the consultant persona) holds 6 across 3 projects covering all
 *    five statuses, so /my/board is neither empty nor single-project;
 *  - van Dijk holds 9, of which 5 are client-visible — and all three of its
 *    Backlog rows are internal, so the client's Backlog column is empty BY
 *    CONSTRUCTION (that emptiness is the AC-2.5 demonstration, not a bug);
 *  - `wp-vd-05` is deliberately unassigned and matches `OPEN_TASK` (32 h,
 *    field mapping), so the board and the allocation surface agree;
 *  - no count here restates `VAN_DIJK.build` (18 / 26).
 */
export const WORK_PACKAGES: readonly WorkPackage[] = [
  { id: 'wp-vd-01', projectId: 'van-dijk', title: 'Pricing & promotions module config', phase: 'build', status: 'in-progress', assigneeId: 'pieter-bakker', clientVisible: true, hoursPlanned: 64, hoursSpent: 41, dueDate: '2026-09-11', agentAssistedBy: 'module-scaffolder' },
  { id: 'wp-vd-02', projectId: 'van-dijk', title: 'Store master data extract — Van Dijk IT', phase: 'build', status: 'blocked', assigneeId: 'anouk-de-vries', clientVisible: true, hoursPlanned: 24, hoursSpent: 18, dueDate: '2026-09-04', agentAssistedBy: null },
  { id: 'wp-vd-03', projectId: 'van-dijk', title: 'POS receipt layout — 4 store formats', phase: 'build', status: 'in-review', assigneeId: 'arta-berisha', clientVisible: true, hoursPlanned: 32, hoursSpent: 30, dueDate: '2026-09-08', agentAssistedBy: 'module-scaffolder' },
  { id: 'wp-vd-04', projectId: 'van-dijk', title: 'Fit-gap sign-off pack — wave 1', phase: 'design', status: 'done', assigneeId: 'anouk-de-vries', clientVisible: true, hoursPlanned: 20, hoursSpent: 20, dueDate: '2026-08-21', agentAssistedBy: null },
  { id: 'wp-vd-05', projectId: 'van-dijk', title: 'Data migration — field mapping & load scripts', phase: 'migrate', status: 'backlog', assigneeId: null, clientVisible: false, hoursPlanned: 32, hoursSpent: 0, dueDate: '2026-09-02', agentAssistedBy: 'mapping-generator' },
  { id: 'wp-vd-06', projectId: 'van-dijk', title: 'Warehouse picking flows — pilot DC', phase: 'build', status: 'in-progress', assigneeId: 'femke-de-boer', clientVisible: true, hoursPlanned: 48, hoursSpent: 19, dueDate: '2026-09-25', agentAssistedBy: null },
  { id: 'wp-vd-07', projectId: 'van-dijk', title: 'Wave 2 staffing note — warehouse skills', phase: 'build', status: 'backlog', assigneeId: 'femke-de-boer', clientVisible: false, hoursPlanned: 8, hoursSpent: 0, dueDate: '2026-09-15', agentAssistedBy: null },
  { id: 'wp-vd-08', projectId: 'van-dijk', title: 'Agent run-cost audit — Build phase', phase: 'build', status: 'in-progress', assigneeId: 'jeroen-smit', clientVisible: false, hoursPlanned: 12, hoursSpent: 5, dueDate: '2026-09-09', agentAssistedBy: null },
  { id: 'wp-vd-09', projectId: 'van-dijk', title: 'Internal margin checkpoint — wave 1', phase: 'build', status: 'backlog', assigneeId: 'niels-jansen', clientVisible: false, hoursPlanned: 6, hoursSpent: 0, dueDate: '2026-09-12', agentAssistedBy: null },

  { id: 'wp-vw-01', projectId: 'van-dijk-wonen', title: 'Blueprint — assortment & pricing', phase: 'design', status: 'in-review', assigneeId: 'marc-dubois', clientVisible: true, hoursPlanned: 44, hoursSpent: 38, dueDate: '2026-09-16', agentAssistedBy: 'blueprint-drafter' },
  { id: 'wp-vw-02', projectId: 'van-dijk-wonen', title: 'Process maps — showroom to delivery', phase: 'discover', status: 'done', assigneeId: 'anouk-de-vries', clientVisible: true, hoursPlanned: 28, hoursSpent: 28, dueDate: '2026-08-20', agentAssistedBy: 'process-mapper' },
  { id: 'wp-vw-03', projectId: 'van-dijk-wonen', title: 'Options costing — 3 scenarios', phase: 'design', status: 'in-progress', assigneeId: 'niels-jansen', clientVisible: false, hoursPlanned: 12, hoursSpent: 7, dueDate: '2026-09-19', agentAssistedBy: null },

  { id: 'wp-vm-01', projectId: 'vermeer', title: 'UAT cycle 2 — outbound flows', phase: 'test', status: 'in-progress', assigneeId: 'blerta-hoxha', clientVisible: true, hoursPlanned: 40, hoursSpent: 26, dueDate: '2026-09-03', agentAssistedBy: 'test-author' },
  { id: 'wp-vm-02', projectId: 'vermeer', title: 'Defect backlog — carrier integration', phase: 'test', status: 'blocked', assigneeId: 'daan-visser', clientVisible: true, hoursPlanned: 30, hoursSpent: 22, dueDate: '2026-09-01', agentAssistedBy: null },
  { id: 'wp-vm-03', projectId: 'vermeer', title: 'Cutover runbook draft', phase: 'go-live', status: 'backlog', assigneeId: 'endrit-krasniqi', clientVisible: true, hoursPlanned: 20, hoursSpent: 0, dueDate: '2026-09-22', agentAssistedBy: 'cutover-planner' },

  { id: 'wp-rd-01', projectId: 'roosendaal', title: 'Recipe & BOM migration — legacy v14', phase: 'migrate', status: 'in-review', assigneeId: 'femke-de-boer', clientVisible: true, hoursPlanned: 36, hoursSpent: 34, dueDate: '2026-09-05', agentAssistedBy: 'mapping-generator' },
  { id: 'wp-rd-02', projectId: 'roosendaal', title: 'Allergen labelling fields', phase: 'build', status: 'blocked', assigneeId: 'femke-de-boer', clientVisible: true, hoursPlanned: 18, hoursSpent: 11, dueDate: '2026-09-02', agentAssistedBy: null },
  { id: 'wp-rd-04', projectId: 'roosendaal', title: 'Upgrade regression pack', phase: 'test', status: 'backlog', assigneeId: 'blerta-hoxha', clientVisible: true, hoursPlanned: 24, hoursSpent: 0, dueDate: '2026-09-29', agentAssistedBy: 'test-author' },

  { id: 'wp-bg-01', projectId: 'bergman', title: 'Q3 upgrade scan — Odoo 18.4', phase: 'maintain', status: 'done', assigneeId: 'femke-de-boer', clientVisible: true, hoursPlanned: 10, hoursSpent: 10, dueDate: '2026-08-14', agentAssistedBy: 'upgrade-scout' },
  { id: 'wp-bg-02', projectId: 'bergman', title: 'Ticket triage — August', phase: 'maintain', status: 'in-progress', assigneeId: 'femke-de-boer', clientVisible: true, hoursPlanned: 16, hoursSpent: 9, dueDate: '2026-08-31', agentAssistedBy: null },
] as const

export const workPackageById = (id: string): WorkPackage => {
  const w = WORK_PACKAGES.find((x) => x.id === id)
  if (!w) throw new Error(`Unknown work package: ${id}`)
  return w
}

/* ── Van Dijk hero facts (screen 3.1/3.3/3.6 headline numbers) ──────────── */

export const VAN_DIJK = {
  requirements: { total: 142, approved: 128, open: 14 },
  build: { modulesDone: 18, modulesTotal: 26 },
  budget: { usedPct: 54, used: 259_000, total: 480_000 },
  goLive: { date: '2026-11-12', label: '12 Nov', weeksAway: 11, status: 'On track' },
  fitGap: { standardPct: 68, libraryPct: 21, newBuildPct: 11, standard: 97, library: 29, newBuild: 16 },
  migration: {
    objects: { total: 14, mapped: 9, inReview: 3, blocked: 2 },
    records: '1.4m',
    validationErrors: { now: 312, was: 4100 },
    trialLoads: { done: 3, total: 4, nextRehearsal: '4 Sep' },
  },
  team: ['anouk-de-vries', 'pieter-bakker', 'endrit-krasniqi', 'arta-berisha'] as const,
} as const

/* ── Agents (screen 2.6 — 23 configured; 9 named) ───────────────────────── */

export type Autonomy = 'L1' | 'L2' | 'L3' | 'L4'

export interface AgentDef {
  id: string
  name: string
  phase: PhaseKey | 'all'
  produces: string
  reviewGate: 'Auto · logged' | 'Review before send' | 'Review before merge' | 'Review before run' | 'Human writes final'
  runs: string
  acceptancePct: number
  defaultAutonomy: Autonomy
}

export const AGENTS: readonly AgentDef[] = [
  { id: 'pax', name: 'Intake listener · PAX', phase: 'discover', produces: 'Requirements into the register, live', reviewGate: 'Auto · logged', runs: 'Live in calls', acceptancePct: 89, defaultAutonomy: 'L3' },
  { id: 'process-mapper', name: 'Process mapper', phase: 'discover', produces: 'Draft process maps', reviewGate: 'Review before merge', runs: 'Per workshop', acceptancePct: 76, defaultAutonomy: 'L2' },
  { id: 'blueprint-drafter', name: 'Blueprint drafter', phase: 'design', produces: 'Solution blueprint sections', reviewGate: 'Review before merge', runs: 'Overnight', acceptancePct: 71, defaultAutonomy: 'L2' },
  { id: 'module-scaffolder', name: 'Module scaffolder', phase: 'build', produces: 'Module scaffolds + config', reviewGate: 'Review before merge', runs: 'Overnight', acceptancePct: 81, defaultAutonomy: 'L2' },
  { id: 'mapping-generator', name: 'Mapping generator', phase: 'migrate', produces: 'Field maps + load scripts', reviewGate: 'Review before run', runs: 'Overnight', acceptancePct: 78, defaultAutonomy: 'L2' },
  { id: 'test-author', name: 'Test author', phase: 'test', produces: 'Test cases from requirements', reviewGate: 'Review before merge', runs: 'Nightly', acceptancePct: 84, defaultAutonomy: 'L3' },
  { id: 'cutover-planner', name: 'Cutover planner', phase: 'go-live', produces: 'Runbook drafts', reviewGate: 'Human writes final', runs: 'On release', acceptancePct: 68, defaultAutonomy: 'L1' },
  { id: 'upgrade-scout', name: 'Upgrade scout', phase: 'maintain', produces: 'Upgrade scans + draft quotes', reviewGate: 'Review before send', runs: 'Continuous', acceptancePct: 74, defaultAutonomy: 'L2' },
  { id: 'handover-agent', name: 'Handover agent', phase: 'all', produces: 'Phase-to-phase context carry', reviewGate: 'Auto · logged', runs: 'On gate', acceptancePct: 91, defaultAutonomy: 'L3' },
] as const

export const AGENTS_CONFIGURED = 23

export const agentById = (id: string): AgentDef => {
  const a = AGENTS.find((x) => x.id === id)
  if (!a) throw new Error(`Unknown agent: ${id}`)
  return a
}

/* ── Home / group stats (screen 0.1 module cards) ───────────────────────── */

export const GROUP_STATS = {
  resources: { people: GROUP_HEADCOUNT, billablePct: 76, openTasksToAllocate: 4 },
  library: { activeProjects: PORTFOLIO.active, assets: 214, agentsConfigured: AGENTS_CONFIGURED },
  myProjects: { assigned: 3, gatesDueThisWeek: 7, blocked: 2 },
} as const

/* ── Allocation (screen 1.4 — the open task + the ranked match) ─────────── */

export interface MatchCandidate {
  personId: string | null // null = new hire / external
  label: string
  detail: string
  fitPct: number | null
  freePct: number | null
  rateLabel: string
  impact: string
  tag: 'Best fit' | 'Cheaper' | 'Growth move' | 'Fallback'
}

export const OPEN_TASK = {
  id: 'task-vd-migration',
  from: 'VAN DIJK RETAIL NL',
  title: 'Data migration — field mapping & load scripts',
  hours: 32,
  skills: 'needs Odoo data migration + retail',
  startsInDays: 6,
  phase: 'phase 4 of 7',
} as const

export const MATCH_CANDIDATES: readonly MatchCandidate[] = [
  { personId: 'julien-roux', label: 'Julien Roux', detail: 'migration, QA', fitPct: 94, freePct: 42, rateLabel: '€96/h', impact: 'No project at risk', tag: 'Best fit' },
  { personId: 'endrit-krasniqi', label: 'Endrit Krasniqi', detail: 'delivery, fit-gap', fitPct: 71, freePct: 18, rateLabel: '€48/h', impact: 'Delays Vermeer UAT by 3 days', tag: 'Cheaper' },
  { personId: 'arta-berisha', label: 'Arta Berisha', detail: 'developer', fitPct: 66, freePct: 55, rateLabel: '€48/h', impact: 'Needs 8 h pairing with Julien', tag: 'Growth move' },
  { personId: null, label: 'New hire / external', detail: '4-week lead time', fitPct: null, freePct: null, rateLabel: '+€38/h', impact: 'Misses the start date', tag: 'Fallback' },
] as const

export const MATCH_POOL_SIZE = 62

/* ── Role lens (10 roles; screens DO change per role — TASK-9F6C8EBE) ───── */

/**
 * The deck asserted "screens do not change per role; permissions do". This
 * ticket makes the honest version true instead: role decides which
 * destinations EXIST, which landing you get and which facts are redacted —
 * not merely which rows are filtered. `src/access/roleAccess.ts` holds the
 * policy; this file holds only the principals.
 */
export interface RoleDef {
  key: RoleKey
  label: string
  side: PrincipalSide
  /** Firm-side roles resolve a Person. */
  personId?: string
  /** Client-side roles resolve a ClientContact. */
  contactId?: string
  /** Platform-side roles resolve an Operator. */
  operatorId?: string
  /** Client-side roles only — the client whose projects they may see. */
  clientId?: string
  /** client-employee only — the single project they are assigned to. */
  projectId?: string
}

/**
 * What the SEED binds to each role — the fictional person, contact, client and
 * project. The `key`, `label` and `side` are NOT repeated here: PX-W6.6 moved
 * the ladder itself to `@/access/roles`, and {@link ROLES} composes the two, so
 * a label or side can never drift between the product ladder and the seed.
 */
const ROLE_SEED_BINDINGS: Readonly<
  Record<RoleKey, Omit<RoleDef, 'key' | 'label' | 'side'> & { label?: string }>
> = {
  'project-manager': { personId: 'anouk-de-vries' },
  consultant: { personId: 'femke-de-boer' },
  manager: { personId: 'niels-jansen' },
  hr: { personId: 'sanne-kuipers' },
  expert: { personId: 'marc-dubois' },
  analyst: { personId: 'lotte-willems' },
  admin: { personId: 'jeroen-smit' },
  'super-admin': { operatorId: 'op-iris' },
  /*
   * The two client-side rows override the PRODUCT label with the seed's own
   * fiction, because a demo screen reading only "Client sponsor" loses the one
   * fact those screens exist to show: whose sponsor. The override lives HERE,
   * inside the demo module, and reaches only concept surfaces through
   * `roleByKey`. Product furniture renders `common:roles.*` instead — W6.6's
   * first cut put this string in `access/roles.ts`, where it landed in the live
   * shell's identity chip for every real client-side session (merged review C1).
   */
  'client-manager': {
    label: 'Client · Van Dijk',
    contactId: 'client-sophie',
    clientId: 'van-dijk-retail',
  },
  'client-employee': {
    label: 'Client user · Van Dijk',
    contactId: 'client-ruben',
    clientId: 'van-dijk-retail',
    projectId: 'van-dijk',
  },
}

export const ROLES: readonly RoleDef[] = ROLE_DEFINITIONS.map((definition) => ({
  ...definition,
  ...ROLE_SEED_BINDINGS[definition.key],
}))

export const roleByKey = (key: RoleKey): RoleDef => {
  const r = ROLES.find((x) => x.key === key)
  if (!r) throw new Error(`Unknown role: ${key}`)
  return r
}

/** True for the two roles that sit on the client's side of the engagement. */
export const isClientSide = (key: RoleKey): boolean => roleByKey(key).side === 'client'

export interface Persona {
  initial: string
  name: string
  meta: string
}

/**
 * The one persona resolver, for all ten roles. Throws on an unresolvable row
 * (matching the `personById` / `projectById` idiom) so a malformed roster
 * fails loudly in `data.test.ts` rather than rendering a blank user chip.
 */
export const personaFor = (key: RoleKey): Persona => {
  const role = roleByKey(key)
  switch (role.side) {
    case 'firm': {
      if (!role.personId) throw new Error(`Firm-side role has no personId: ${key}`)
      const p = personById(role.personId)
      return { initial: p.initial, name: p.name, meta: `${p.role} · ${p.office}` }
    }
    case 'client': {
      if (!role.contactId) throw new Error(`Client-side role has no contactId: ${key}`)
      const c = clientContactById(role.contactId)
      return { initial: c.initial, name: c.name, meta: `${c.role} · ${c.org}` }
    }
    case 'platform': {
      if (!role.operatorId) throw new Error(`Platform-side role has no operatorId: ${key}`)
      const o = operatorById(role.operatorId)
      return { initial: o.initial, name: o.name, meta: `${o.role} · ${o.org}` }
    }
  }
}

/* ── Sign-in identities (TASK-9F6C8EBE §6.9) ────────────────────────────── */

/**
 * The e-mail domains, declared ONCE. An address is composed from a persona's
 * own name plus the domain for its side — never typed into a component (I-18).
 */
export const EMAIL_DOMAINS = {
  firm: 'perpleks.nl',
  client: 'vandijkretail.nl',
  platform: 'px-platform.eu',
} as const

const emailLocalPart = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z ]/g, '')
    .trim()
    .split(/\s+/)
    .join('.')

export interface LoginIdentity {
  role: RoleKey
  email: string
  name: string
  initial: string
  meta: string
}

/**
 * Exactly one identity per ROLES entry, every field resolved through
 * `personaFor` — nothing about an identity is typed anywhere else.
 *
 * ⚠ There is no password anywhere in this codebase. Signing in SELECTS an
 * identity; it verifies nothing, stores no credential and calls nothing (I-17).
 */
export const LOGIN_IDENTITIES: readonly LoginIdentity[] = ROLES.map((role) => {
  const persona = personaFor(role.key)
  return {
    role: role.key,
    email: `${emailLocalPart(persona.name)}@${EMAIL_DOMAINS[role.side]}`,
    name: persona.name,
    initial: persona.initial,
    meta: persona.meta,
  }
})

export const loginIdentityFor = (role: RoleKey): LoginIdentity => {
  const identity = LOGIN_IDENTITIES.find((x) => x.role === role)
  if (!identity) throw new Error(`No login identity for role: ${role}`)
  return identity
}
